import * as Yup from "yup"
import {
     CONST_BUSINESS_UNIT_AERO_ID,
     CONST_BUSINESS_UNIT_AUTRE_ID,
     CONST_CONSULTANT_MISSION_FREQUENCE_HABITUELLE,
     CONST_CONSULTANT_MISSION_FREQUENCE_OCCASIONNEL,
     CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR,
} from "@common-constants/*"
import { Form, Formik, FormikHelpers } from "formik"
import MyAlert from "@common-utils/MyAlert"
import React, { useState } from "react"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import MyCard from "@common-utils/MyCard"
import { useDemandeEmbaucheDetailsContext } from "../DemandeEmbaucheDetails"
import {
     IGetDemandeEmbaucheInfoClientResponseModel,
     ISetDemandeEmbaucheInfoClientNonSalarieFormikValuesModel,
     ISetDemandeEmbaucheInfoClientNonSalarieRequestModel,
} from "./core/_models"
import { AxiosError } from "axios"
import { toast } from "react-toastify"
import { setDemandeEmbaucheInfoClientNonSalarie } from "./core/_requests"
import { IClientPropalModel, IConsultantModel, IHttpErrorResponseModel } from "@common-models/*"
import MySelectGetAllDataFromServer from "@common-utils/fields/MySelectGetAllDataFromServer"
import {
     CONST_API_UTILS_GET_BASES_FACTU_ENDPOINT,
     CONST_API_UTILS_GET_BUSINESS_UNITS_ENDPOINT,
     CONST_API_UTILS_GET_MACHINES_ENDPOINT,
     CONST_API_UTILS_GET_PTFS_NON_MERE_ENDPOINT,
} from "../../../../../endpoints"
import MyCheckBoxField from "@common-utils/fields/MyCheckBoxField"
import MyDateDayMonthYearField from "@common-utils/fields/MyDateDayMonthYearField"
import { getClientPropalSimpleDetailsRequest } from "../../../../client/ptf/core/_requests"
import { useQueryClient } from "react-query"
import MyModal from "@common-utils/MyModal"
import AddMachineForm from "../../../../utils/_AddMachineForm"
import MySelectGetAllDataFromServerFormField from "@common-components/fields/MySelectGetAllDataFromServerFormField"
import MyInputFormField from "@common-components/fields/MyInputFormField"
import MyCheckboxFormField from "@common-components/fields/MyCheckboxFormField"
import MySelectFormField from "@common-components/fields/MySelectFormField"
import MyTextareaFormField from "@common-components/fields/MyTextareaFormField"

const DemandeEmbaucheDetailsInfoClientNonSalarie = ({ data }: { data: IGetDemandeEmbaucheInfoClientResponseModel }) => {
     const queryClient = useQueryClient()

     const context = useDemandeEmbaucheDetailsContext()
     const [propalSimpleInfo, setPropalSimpleInfo] = useState<{ loading: boolean; data: IClientPropalModel | null }>({
          loading: false,
          data: data.consultant.clientPropal || null,
     })
     const [isAddingMachine, setIsAddingMachine] = useState<boolean>(false)

     // Form schema
     const schema = Yup.object().shape({
          clientPropal: Yup.string().label(`"PTF"`).nullable().required(),
          missionDateDebut: Yup.string().label(`"Date de début"`).nullable().required(),
          missionDateFin: Yup.string().label(`"Date de fin"`).nullable().required(),
          isMachineIncluded: Yup.boolean().label(`"Machine?"`).nullable().required(),
          machine: Yup.number()
               .label(`"Machine"`)
               .nullable()
               .when("isMachineIncluded", {
                    is: true,
                    then: schema => schema.required(),
               }),
          assuranceBu: Yup.number().label("Secteur d'activité").required(),
          assuranceBuAutrePrecision: Yup.string()
               .label("Précisez le secteur")
               .when("assuranceBu", {
                    is: CONST_BUSINESS_UNIT_AUTRE_ID,
                    then: schema => schema.required(),
               }),
          assuranceBuIfAeroIsInterventionDansAvion: Yup.boolean()
               .label("Le consultant intervient dans avion ?")
               .when("assuranceBu", {
                    is: CONST_BUSINESS_UNIT_AERO_ID,
                    then: schema => schema.required(),
               }),
          assuranceBuIfAeroFrequenceInterventionDansAvion: Yup.string()
               .label("Fréquence d'intervention dans l'avion")
               .nullable()
               .when("assuranceBuIfAeroIsInterventionDansAvion", {
                    is: true,
                    then: schema => schema.required(),
               }),
          assuranceIsDetonique: Yup.boolean().label("S'agit t-il d'une Activité détonique / explosif ?").required(),
          assuranceIsBureauEtude: Yup.boolean().label("S'agit t-il d'un bureau d'études ?").required(),
          assuranceIsVehiculeConcerned: Yup.boolean().label("Un Véhicule est concerné ?").required(),
          assuranceTypeOfVehicule: Yup.string()
               .label("Type de véhicule")
               .when("assuranceIsVehiculeConcerned", {
                    is: true,
                    then: schema => schema.required(),
               }),
          assuranceUsageFrequencyOfVehicule: Yup.string()
               .label("Fréquence d'utilisation")
               .when("assuranceIsVehiculeConcerned", {
                    is: true,
                    then: schema => schema.required(),
               }),
          assuranceIsVehiculeGoingToCarryEquipments: Yup.boolean()
               .label("Y a t il des équipements à transporter dans le véhicule ?")
               .when("assuranceIsVehiculeConcerned", {
                    is: true,
                    then: schema => schema.required(),
               }),
          assuranceValueOfEquipementsCarriedByVehicule: Yup.number()
               .label("Montant à assurer")
               .when("assuranceIsVehiculeGoingToCarryEquipments", {
                    is: true,
                    then: schema => schema.required(),
               }),
          assuranceIsEquipmentConcerned: Yup.boolean().label("Un équipement est concerné ?").required(),
          assuranceEquipment: Yup.string()
               .label("Équipement")
               .when("assuranceIsEquipmentConcerned", {
                    is: true,
                    then: schema => schema.required(),
               }),
          assuranceUsageFrequencyOfEquipment: Yup.string()
               .label("Fréquence d'utilisation")
               .when("assuranceIsEquipmentConcerned", {
                    is: true,
                    then: schema => schema.required(),
               }),
          assuranceRemarques: Yup.string().label("Remarques"),
     })

     // Form initial values
     const initialValues: ISetDemandeEmbaucheInfoClientNonSalarieFormikValuesModel = {
          clientPropal: data.consultant.clientPropal?.id,
          missionDateDebut: data.mission?.dateDebutValidite?.format("YYYY-MM-DD"),
          missionDateFin: data.mission?.dateFinValidite?.format("YYYY-MM-DD"),
          missionBaseFacturationAnnuelle: data.consultant.baseFacturationAnnuelle?.id,
          isMachineIncluded: !!data.consultant.machine,
          machine: data.consultant.machine?.id,
          assuranceBu: data.mission?.assuranceBu?.id || undefined,
          assuranceBuAutrePrecision: data.mission?.assuranceBuAutrePrecision || undefined,
          assuranceBuIfAeroIsInterventionDansAvion: !!data.mission?.assuranceBuIfAeroIsInterventionDansAvion,
          assuranceBuIfAeroFrequenceInterventionDansAvion: data.mission?.assuranceBuIfAeroFrequenceInterventionDansAvion || undefined,
          assuranceIsDetonique: !!data.mission?.assuranceIsDetonique,
          assuranceIsBureauEtude: !!data.mission?.assuranceIsBureauEtude,
          assuranceIsVehiculeConcerned: !!data.mission?.assuranceIsVehiculeConcerned,
          assuranceTypeOfVehicule: data.mission?.assuranceTypeOfVehicule || undefined,
          assuranceUsageFrequencyOfVehicule: data.mission?.assuranceUsageFrequencyOfVehicule || undefined,
          assuranceIsVehiculeGoingToCarryEquipments: !!data.mission?.assuranceIsVehiculeGoingToCarryEquipments,
          assuranceValueOfEquipementsCarriedByVehicule:
               data.mission?.assuranceValueOfEquipementsCarriedByVehicule === null ? undefined : data.mission?.assuranceValueOfEquipementsCarriedByVehicule,
          assuranceIsEquipmentConcerned: !!data.mission?.assuranceIsEquipmentConcerned,
          assuranceEquipment: data.mission?.assuranceEquipment || undefined,
          assuranceUsageFrequencyOfEquipment: data.mission?.assuranceUsageFrequencyOfEquipment || undefined,
          assuranceRemarques: data.mission?.assuranceRemarques || undefined,
     }

     // Handles submit
     function handleSubmit(values: ISetDemandeEmbaucheInfoClientNonSalarieFormikValuesModel, helpers: FormikHelpers<ISetDemandeEmbaucheInfoClientNonSalarieFormikValuesModel>) {
          helpers.setStatus(null)

          const payload: ISetDemandeEmbaucheInfoClientNonSalarieRequestModel = {
               clientPropal: values.clientPropal as number,
               missionDateDebut: values.missionDateDebut as string,
               missionDateFin: values.missionDateFin as string,
               missionBaseFacturationAnnuelle: values.missionBaseFacturationAnnuelle as number,
               isMachineIncluded: values.isMachineIncluded,
               machine: values.machine,
               assuranceBu: values.assuranceBu,
               assuranceBuAutrePrecision: values.assuranceBuAutrePrecision,
               assuranceBuIfAeroIsInterventionDansAvion: values.assuranceBuIfAeroIsInterventionDansAvion,
               assuranceBuIfAeroFrequenceInterventionDansAvion: values.assuranceBuIfAeroFrequenceInterventionDansAvion,
               assuranceIsDetonique: values.assuranceIsDetonique,
               assuranceIsBureauEtude: values.assuranceIsBureauEtude,
               assuranceIsVehiculeConcerned: values.assuranceIsVehiculeConcerned,
               assuranceTypeOfVehicule: values.assuranceTypeOfVehicule,
               assuranceUsageFrequencyOfVehicule: values.assuranceUsageFrequencyOfVehicule,
               assuranceIsVehiculeGoingToCarryEquipments: values.assuranceIsVehiculeGoingToCarryEquipments,
               assuranceValueOfEquipementsCarriedByVehicule: values.assuranceValueOfEquipementsCarriedByVehicule,
               assuranceIsEquipmentConcerned: values.assuranceIsEquipmentConcerned,
               assuranceEquipment: values.assuranceEquipment,
               assuranceUsageFrequencyOfEquipment: values.assuranceUsageFrequencyOfEquipment,
               assuranceRemarques: values.assuranceRemarques,
          }

          setDemandeEmbaucheInfoClientNonSalarie(data.consultant.id as number, payload)
               .then(() => {
                    toast.success("Vos modifications ont bien été prises en compte.")
                    context.setNavigationStep("doc")
                    queryClient.setQueryData<IConsultantModel | undefined>(context.REACT_QUERY_KEY_GET_DE_CONTRAT_ETAT_STATUT, (prev: IConsultantModel | undefined) => {
                         if (prev) {
                              prev = {
                                   ...prev,
                                   clientPropal: { id: values.clientPropal, ...prev.clientPropal },
                              }
                         }
                         return prev
                    })
               })
               .catch((e: AxiosError) => {
                    const error: IHttpErrorResponseModel = e.response?.data

                    // Set form errors
                    if (error?.code === CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR && error?.errors) {
                         for (const key in error.errors) helpers.setFieldError(key, error.errors[key])
                    }

                    // Set form global status and notify user using a toast
                    helpers.setStatus(error?.detail)
                    toast.error(error?.detail, { autoClose: false })

                    // Stop submit loader
                    helpers.setSubmitting(false)
               })
               .finally(() => helpers.setSubmitting(false))
     }

     // Handles change PTF
     function handleChangeClientPropal(ptf_id: number | undefined | null) {
          if (ptf_id) {
               setPropalSimpleInfo(prev => ({ ...prev, loading: true }))
               getClientPropalSimpleDetailsRequest(ptf_id)
                    .then(r => {
                         setPropalSimpleInfo({ loading: false, data: r.data })
                    })
                    .catch(() => {
                         setPropalSimpleInfo(prev => ({ ...prev, loading: false }))
                         toast.error("Un problème est survenu lors de la récupération des informations de la PTF sélectionnée.", { autoClose: false })
                    })
          }
     }

     return (
          <>
               <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema}>
                    {helpers => {
                         return (
                              <MyCard classNames={"w-100 w-lg-75 w-xl-60"}>
                                   <MyCard.Body>
                                        <Form noValidate autoComplete="off">
                                             {helpers.status && (
                                                  <MyAlert type={"danger"} classNames={"mb-4"}>
                                                       {helpers.status}
                                                  </MyAlert>
                                             )}

                                             <div className="d-flex justify-content-between align-items-center mb-2">
                                                  <h3>Informations client</h3>
                                             </div>
                                             <div className="separator separator-dashed mb-6" />

                                             {/* PTF */}
                                             <div className="row mb-4">
                                                  <MyAlert type={"primary"} classNames={"mb-4"}>
                                                       Assurez-vous que la PTF soit bien validée afin qu'elle puisse apparaître dans la liste
                                                  </MyAlert>
                                                  <label className="col-lg-4 col-form-label fw-bold fs-6 required">Choisissez la PTF</label>
                                                  <div className="col-lg-8">
                                                       <MySelectGetAllDataFromServer
                                                            name={"clientPropal"}
                                                            value={helpers.values.clientPropal}
                                                            defaultOptions={
                                                                 data.consultant.clientPropal
                                                                      ? [
                                                                             {
                                                                                  value: data.consultant.clientPropal.id as number,
                                                                                  label: data.consultant.clientPropal.referencePlusPlus as string,
                                                                             },
                                                                        ]
                                                                      : []
                                                            }
                                                            isInvalid={!!helpers.errors.clientPropal}
                                                            onChange={val => {
                                                                 helpers.setFieldValue("clientPropal", val).then(() => {
                                                                      handleChangeClientPropal(val as number)
                                                                 })
                                                            }}
                                                            url={CONST_API_UTILS_GET_PTFS_NON_MERE_ENDPOINT}
                                                            isClearable={false}
                                                       />
                                                       {helpers.errors.clientPropal && <div className={"text-danger"}>{helpers.errors.clientPropal}</div>}
                                                  </div>
                                             </div>

                                             {propalSimpleInfo.loading && (
                                                  <div className={"text-center p-5"}>
                                                       <MySimpleSpinner size={"md"} />{" "}
                                                  </div>
                                             )}

                                             {propalSimpleInfo.data && (
                                                  <>
                                                       {/* Détails PTF (loaded) */}
                                                       <div className={"p-4 border border-dashed text-gray-600 mb-4"}>
                                                            <h4 className={"mb-4 text-gray-600"}>
                                                                 Détails de la {propalSimpleInfo.data.reference}
                                                                 <i className="fas fa-info-circle fs-3 float-end" />
                                                            </h4>
                                                            <div className="separator separator-dashed mb-6" />

                                                            <p>Client: {propalSimpleInfo.data.client?.nom}</p>
                                                            <p>Responsable client: {propalSimpleInfo.data.clientResponsable?.prenomNom}</p>
                                                            {propalSimpleInfo.data.type === "TYPE_ASSISTANCE_TECHNIQUE" && <p>TJM: {propalSimpleInfo.data.tjm}€</p>}
                                                            <p>Type: {propalSimpleInfo.data.typeLabel}</p>
                                                       </div>

                                                       {/* MISSION */}
                                                       <div className={"p-4 border border-dashed mb-4"}>
                                                            <h4 className={"mb-4"}>MISSION</h4>
                                                            <div className="separator separator-dashed mb-6" />

                                                            {/* Période mission */}
                                                            <div className="row mb-4">
                                                                 <label className="col-lg-4 col-form-label fw-bold fs-6 required">Période</label>

                                                                 <div className="col-lg-8">
                                                                      <div className="d-flex">
                                                                           <MyDateDayMonthYearField
                                                                                name={"missionDateDebut"}
                                                                                value={helpers.values.missionDateDebut}
                                                                                isInvalid={!!helpers.errors.missionDateDebut}
                                                                                minDate={data.consultant.dateEmbauche?.format("YYYY-MM-DD")}
                                                                                maxDate={helpers.values.missionDateFin}
                                                                                onChange={val => helpers.setFieldValue("missionDateDebut", val)}
                                                                                placeholder={"Date de début"}
                                                                                classNames={"rounded-0 rounded-start"}
                                                                           />
                                                                           <span className="input-group-text fas fa-ellipsis d-flex align-items-center rounded-0" />
                                                                           <MyDateDayMonthYearField
                                                                                name={"missionDateFin"}
                                                                                value={helpers.values.missionDateFin}
                                                                                minDate={helpers.values.missionDateDebut}
                                                                                isInvalid={!!helpers.errors.missionDateFin}
                                                                                onChange={val => helpers.setFieldValue("missionDateFin", val)}
                                                                                placeholder={"Date de fin"}
                                                                                classNames={"rounded-0 rounded-end"}
                                                                           />
                                                                      </div>
                                                                      {(helpers.errors.missionDateDebut || helpers.errors.missionDateFin) && (
                                                                           <div className={"text-danger"}>Période requise ou incorrecte</div>
                                                                      )}
                                                                 </div>
                                                            </div>

                                                            {/* Base de facturation */}
                                                            <div className="row mb-4">
                                                                 <label className="col-lg-4 col-form-label fw-bold fs-6 required">Base de facturation annuelle</label>

                                                                 <div className="col-lg-8">
                                                                      <MySelectGetAllDataFromServer
                                                                           name={"missionBaseFacturationAnnuelle"}
                                                                           value={helpers.values.missionBaseFacturationAnnuelle}
                                                                           defaultOptions={
                                                                                data.consultant.baseFacturationAnnuelle
                                                                                     ? [
                                                                                            {
                                                                                                 value: data.consultant.baseFacturationAnnuelle.id as number,
                                                                                                 label: data.consultant.baseFacturationAnnuelle.nbJoursPlusPlus!.toString(),
                                                                                            },
                                                                                       ]
                                                                                     : []
                                                                           }
                                                                           url={CONST_API_UTILS_GET_BASES_FACTU_ENDPOINT}
                                                                           isInvalid={!!helpers.errors.missionBaseFacturationAnnuelle}
                                                                           onChange={val => helpers.setFieldValue("missionBaseFacturationAnnuelle", val)}
                                                                      />
                                                                      {helpers.errors.missionBaseFacturationAnnuelle && (
                                                                           <div className={"text-danger"}>{helpers.errors.missionBaseFacturationAnnuelle}</div>
                                                                      )}
                                                                 </div>
                                                            </div>

                                                            {/* Voulez-vous inclure une machine? */}
                                                            <div className="row mb-4">
                                                                 <label className="col-lg-4 col-form-label fw-bold fs-6">Voulez-vous inclure une machine?</label>

                                                                 <div className="col-lg-8 d-flex align-items-center">
                                                                      <MyCheckBoxField
                                                                           name={"isMachineIncluded"}
                                                                           value={helpers.values.isMachineIncluded}
                                                                           isInvalid={!!helpers.errors.isMachineIncluded}
                                                                           onChange={val => helpers.setFieldValue("isMachineIncluded", val)}
                                                                      />
                                                                      {helpers.errors.isMachineIncluded && <div className={"text-danger"}>{helpers.errors.isMachineIncluded}</div>}
                                                                 </div>
                                                            </div>

                                                            {helpers.values.isMachineIncluded && (
                                                                 <>
                                                                      {/* Machine */}
                                                                      <div className="row mb-4">
                                                                           <label className="col-lg-4 col-form-label fw-bold fs-6 required">Choisissez une machine</label>

                                                                           <div className="col-lg-8">
                                                                                <MySelectGetAllDataFromServer
                                                                                     name={"machine"}
                                                                                     value={helpers.values.machine}
                                                                                     defaultOptions={
                                                                                          data.consultant.machine
                                                                                               ? [
                                                                                                      {
                                                                                                           value: data.consultant.machine.id as number,
                                                                                                           label: data.consultant.machine.nomPlusPlus as string,
                                                                                                      },
                                                                                                 ]
                                                                                               : []
                                                                                     }
                                                                                     isInvalid={!!helpers.errors.machine}
                                                                                     onChange={val => helpers.setFieldValue("machine", val)}
                                                                                     url={CONST_API_UTILS_GET_MACHINES_ENDPOINT}
                                                                                />
                                                                                <div className="d-flex justify-content-between flex-row-reverse mt-1">
                                                                                     {helpers.errors.machine && <div className={"text-danger"}>{helpers.errors.machine}</div>}
                                                                                     <div className="text-primary fw-bold cursor-pointer" onClick={() => setIsAddingMachine(true)}>
                                                                                          Ajouter <i className={"fas fa-plus-circle text-primary"} />
                                                                                     </div>
                                                                                </div>
                                                                           </div>
                                                                      </div>
                                                                 </>
                                                            )}
                                                       </div>
                                                  </>
                                             )}

                                             <div className="p-4 border border-dashed mb-4">
                                                  <h4 className={"mb-4"}>ASSURANCE</h4>
                                                  <div className="separator separator-dashed mb-6" />

                                                  <MyAlert type={"info"} classNames={"mb-4"}>
                                                       L’aspect assurance est critique pour la société et peut avoir des conséquences financières importantes en cas de
                                                       non-couverture. Le service administratif vous contactera afin de vérifier que toutes les informations nécessaires ont bien
                                                       été complétées.
                                                  </MyAlert>

                                                  <MySelectGetAllDataFromServerFormField
                                                       name={"assuranceBu"}
                                                       formikProps={helpers}
                                                       endpoint={CONST_API_UTILS_GET_BUSINESS_UNITS_ENDPOINT}
                                                       defaultOptions={
                                                            data.mission?.assuranceBu
                                                                 ? [
                                                                        {
                                                                             value: data.mission!.assuranceBu!.id!,
                                                                             label: data.mission!.assuranceBu!.nom!,
                                                                        },
                                                                   ]
                                                                 : []
                                                       }
                                                       placeholder={"Secteur d'activité"}
                                                       method={"GET"}
                                                       label={<span className={"required"}>Type d'activité</span>}
                                                       classNameLayout={"mb-2"}
                                                       classNameLabel={"col-lg-4"}
                                                       classNameField={"col-lg-8"}
                                                  />
                                                  {helpers.values.assuranceBu === CONST_BUSINESS_UNIT_AUTRE_ID && (
                                                       <MyInputFormField
                                                            name={"assuranceBuAutrePrecision"}
                                                            formikProps={helpers}
                                                            placeholder={"Précisez le secteur"}
                                                            label={<span className={"required"}>Précisez le secteur</span>}
                                                            classNameLayout={"mb-2"}
                                                            classNameLabel={"col-lg-4"}
                                                            classNameField={"col-lg-8"}
                                                       />
                                                  )}

                                                  {helpers.values.assuranceBu === CONST_BUSINESS_UNIT_AERO_ID && (
                                                       <div className={"p-4 border border-dashed mb-4"}>
                                                            <h5 className={"mb-4"}>Aerospace</h5>
                                                            <div className="separator separator-dashed mb-6" />
                                                            <MyCheckboxFormField
                                                                 name={"assuranceBuIfAeroIsInterventionDansAvion"}
                                                                 formikProps={helpers}
                                                                 label={"Des interventions sont-elles prévues à bord, à proximité ou aux abords de l’avion ?"}
                                                                 classNameLayout={"mb-2"}
                                                                 classNameLabel={"col-lg-4"}
                                                                 classNameField={"col-lg-8"}
                                                            />
                                                            {helpers.values.assuranceBuIfAeroIsInterventionDansAvion && (
                                                                 <MySelectFormField
                                                                      name={"assuranceBuIfAeroFrequenceInterventionDansAvion"}
                                                                      formikProps={helpers}
                                                                      options={[
                                                                           { value: CONST_CONSULTANT_MISSION_FREQUENCE_OCCASIONNEL, label: "Occasionnelle" },
                                                                           { value: CONST_CONSULTANT_MISSION_FREQUENCE_HABITUELLE, label: "Habituelle" },
                                                                      ]}
                                                                      placeholder={"Fréquence d'intervention"}
                                                                      label={<span className="required">Fréquence d'intervention</span>}
                                                                      classNameLayout={"mb-2"}
                                                                      classNameLabel={"col-lg-4"}
                                                                      classNameField={"col-lg-8"}
                                                                 />
                                                            )}
                                                       </div>
                                                  )}

                                                  <MyCheckboxFormField
                                                       name={"assuranceIsDetonique"}
                                                       formikProps={helpers}
                                                       label={"Activité détonique / explosif"}
                                                       classNameLayout={"mb-2 mt-5"}
                                                       classNameLabel={"col-lg-4"}
                                                       classNameField={"col-lg-8"}
                                                  />
                                                  <MyCheckboxFormField
                                                       name={"assuranceIsBureauEtude"}
                                                       formikProps={helpers}
                                                       label={"Bureau d'études"}
                                                       classNameLayout={"mb-2 mt-5"}
                                                       classNameLabel={"col-lg-4"}
                                                       classNameField={"col-lg-8"}
                                                  />
                                                  <MyCheckboxFormField
                                                       name={"assuranceIsVehiculeConcerned"}
                                                       formikProps={helpers}
                                                       label={`Utilisera t-${data.consultant.civilite === "CIVILITE_MR" ? "il" : "elle"} un véhicule client ?`}
                                                       classNameLayout={"mb-4 mt-5"}
                                                       classNameLabel={"col-lg-4"}
                                                       classNameField={"col-lg-8"}
                                                  />
                                                  {helpers.values.assuranceIsVehiculeConcerned && (
                                                       <div className={"p-4 border border-dashed mb-4"}>
                                                            <h5 className={"mb-4"}>Véhicule</h5>
                                                            <div className="separator separator-dashed mb-6" />
                                                            <MyInputFormField
                                                                 name={"assuranceTypeOfVehicule"}
                                                                 formikProps={helpers}
                                                                 placeholder={"Type de véhicule"}
                                                                 label={<span className={"required"}>Type de véhicule</span>}
                                                                 classNameLayout={"mb-2"}
                                                                 classNameLabel={"col-lg-4"}
                                                                 classNameField={"col-lg-8"}
                                                            />

                                                            <MySelectFormField
                                                                 name={"assuranceUsageFrequencyOfVehicule"}
                                                                 formikProps={helpers}
                                                                 options={[
                                                                      { value: CONST_CONSULTANT_MISSION_FREQUENCE_OCCASIONNEL, label: "Occasionnelle" },
                                                                      { value: CONST_CONSULTANT_MISSION_FREQUENCE_HABITUELLE, label: "Habituelle" },
                                                                 ]}
                                                                 placeholder={"Fréquence d'utilisation"}
                                                                 label={<span className="required">Fréquence d'utilisation</span>}
                                                                 classNameLayout={"mb-2"}
                                                                 classNameLabel={"col-lg-4"}
                                                                 classNameField={"col-lg-8"}
                                                            />

                                                            <MyCheckboxFormField
                                                                 name={"assuranceIsVehiculeGoingToCarryEquipments"}
                                                                 formikProps={helpers}
                                                                 label={"Y a t-il des équipements à transporter dans le véhicule ?"}
                                                                 classNameLayout={"mb-2 mt-5"}
                                                                 classNameLabel={"col-lg-4"}
                                                                 classNameField={"col-lg-8"}
                                                            />

                                                            {helpers.values.assuranceIsVehiculeGoingToCarryEquipments && (
                                                                 <MyInputFormField
                                                                      name={"assuranceValueOfEquipementsCarriedByVehicule"}
                                                                      type={"number"}
                                                                      formikProps={helpers}
                                                                      placeholder={"Montant à garantir des équipements"}
                                                                      label={<span className="required">Montant à garantir des équipements</span>}
                                                                      classNameLayout={"mb-2"}
                                                                      classNameLabel={"col-lg-4"}
                                                                      classNameField={"col-lg-8"}
                                                                 />
                                                            )}
                                                       </div>
                                                  )}

                                                  <MyCheckboxFormField
                                                       name={"assuranceIsEquipmentConcerned"}
                                                       formikProps={helpers}
                                                       label={"Utilisation d'une machine ou équipement client"}
                                                       classNameLayout={"mb-4 mt-5"}
                                                       classNameLabel={"col-lg-4"}
                                                       classNameField={"col-lg-8"}
                                                  />
                                                  {helpers.values.assuranceIsEquipmentConcerned && (
                                                       <div className={"p-4 border border-dashed mb-4"}>
                                                            <h5 className={"mb-4"}>Machine / équipement</h5>
                                                            <div className="separator separator-dashed mb-6" />
                                                            <MyInputFormField
                                                                 name={"assuranceEquipment"}
                                                                 formikProps={helpers}
                                                                 placeholder={"Équipement"}
                                                                 label={<span className={"required"}>Machine / équipement</span>}
                                                                 classNameLayout={"mb-2 "}
                                                                 classNameLabel={"col-lg-4"}
                                                                 classNameField={"col-lg-8"}
                                                            />

                                                            <MySelectFormField
                                                                 name={"assuranceUsageFrequencyOfEquipment"}
                                                                 formikProps={helpers}
                                                                 options={[
                                                                      { value: CONST_CONSULTANT_MISSION_FREQUENCE_OCCASIONNEL, label: "Occasionnelle" },
                                                                      { value: CONST_CONSULTANT_MISSION_FREQUENCE_HABITUELLE, label: "Habituelle" },
                                                                 ]}
                                                                 placeholder={"Fréquence d'utilisation"}
                                                                 label={<span className="required">Fréquence d'utilisation</span>}
                                                                 classNameLayout={"mb-2"}
                                                                 classNameLabel={"col-lg-4"}
                                                                 classNameField={"col-lg-8"}
                                                            />
                                                       </div>
                                                  )}

                                                  <MyTextareaFormField
                                                       name={"assuranceRemarques"}
                                                       formikProps={helpers}
                                                       placeholder={"Activités annexes / missions complémentaires / précisions sur les activités pouvant nécessiter une assurance"}
                                                       label={"Remarques"}
                                                       minRows={5}
                                                       maxRows={10}
                                                       classNameLayout={"mb-2"}
                                                       classNameLabel={"col-lg-4"}
                                                       classNameField={"col-lg-8"}
                                                  />
                                             </div>

                                             <div className="separator bg-primary my-5" />

                                             {/* Submit button */}
                                             <div className={"d-flex justify-content-between"}>
                                                  <button
                                                       className={"btn btn-sm btn-warning d-inline-flex align-items-center"}
                                                       type={"button"}
                                                       disabled={helpers.isSubmitting}
                                                       onClick={() => context.setNavigationStep("contract")}
                                                  >
                                                       <span className={"fas fa-arrow-left fs-4 me-1"}></span> Précédant
                                                  </button>
                                                  <button className={"btn btn-sm btn-primary d-inline-flex align-items-center"} type={"submit"} disabled={helpers.isSubmitting}>
                                                       {!helpers.isSubmitting ? (
                                                            <>
                                                                 Sauvegarder et suivant <span className={"fas fa-arrow-right fs-4 ms-1"}></span>
                                                            </>
                                                       ) : (
                                                            <MySimpleSpinner size={"sm"} />
                                                       )}
                                                  </button>
                                             </div>
                                        </Form>
                                   </MyCard.Body>
                              </MyCard>
                         )
                    }}
               </Formik>

               {/* Modal add machine */}
               {isAddingMachine && (
                    <MyModal title={<>Nouvelle machine</>} show={true} onCloseCallback={() => setIsAddingMachine(false)}>
                         <AddMachineForm callback={() => setIsAddingMachine(false)} />
                    </MyModal>
               )}
          </>
     )
}

export default DemandeEmbaucheDetailsInfoClientNonSalarie
